import type { DocType } from "./types.js";

// ─── Valid status transitions per doc type ───────────────────────────────────

export const STATUS_TRANSITIONS: Record<DocType, Record<string, string[]>> = {
  adr: {
    draft: ["proposed"],
    proposed: ["accepted", "rejected"],
    accepted: ["superseded", "deprecated"],
    rejected: [],
    superseded: [],
    deprecated: [],
  },
  prd: {
    draft: ["review"],
    review: ["approved", "rejected"],
    approved: ["in-progress"],
    rejected: [],
    "in-progress": ["delivered", "cancelled"],
    delivered: ["deprecated"],
    cancelled: [],
    deprecated: [],
  },
  rfc: {
    draft: ["discussion"],
    discussion: ["accepted", "rejected", "withdrawn"],
    // `accepted` means the shape was agreed, not that anything exists yet — an
    // RFC being built has a state of its own.
    accepted: ["in-progress", "superseded"],
    "in-progress": ["delivered", "superseded"],
    delivered: ["superseded"],
    rejected: [],
    withdrawn: [],
    superseded: [],
  },
  tdd: {
    draft: ["review"],
    review: ["approved"],
    // TDDs are routinely phased, and phases ship over weeks. Without
    // `in-progress` a half-built TDD had to sit at `approved` (reads as "nothing
    // started") or `delivered` (reads as "done"), so the frontmatter said
    // something untrue and the real state hid in prose.
    approved: ["in-progress", "deprecated"],
    "in-progress": ["delivered", "deprecated"],
    delivered: ["deprecated"],
    deprecated: [],
  },
  runbook: {
    draft: ["active"],
    active: ["needs-update", "deprecated", "archived"],
    "needs-update": ["active"],
    deprecated: [],
    archived: [],
  },
  postmortem: {
    draft: ["review"],
    review: ["published"],
    published: ["archived"],
    archived: [],
  },
  slo: {
    draft: ["active"],
    active: ["deprecated"],
    deprecated: [],
  },
  "service-catalog": {
    planned: ["experimental"],
    experimental: ["active"],
    active: ["deprecated"],
    deprecated: ["archived"],
    archived: [],
  },
  deprecation: {
    planned: ["active"],
    active: ["completed"],
    completed: [],
  },
  "tech-debt": {
    identified: ["accepted"],
    accepted: ["in-progress"],
    "in-progress": ["resolved", "accepted-risk"],
    resolved: [],
    "accepted-risk": [],
  },
  "feature-brief": {
    draft: ["review"],
    review: ["approved", "rejected", "escalated-to-prd"],
    approved: ["in-progress"],
    "in-progress": ["delivered"],
    delivered: [],
    rejected: [],
    "escalated-to-prd": [],
  },
  "api-contract": {
    draft: ["alpha"],
    alpha: ["beta"],
    beta: ["stable"],
    stable: ["deprecated"],
    deprecated: [],
  },
  "decision-log": {
    draft: ["accepted"],
    accepted: ["superseded"],
    superseded: [],
  },
  "roadmap-snapshot": {
    draft: ["active"],
    active: ["archived"],
    archived: [],
  },
  "quarterly-review": {
    draft: ["active"],
    active: ["archived"],
    archived: [],
  },
  onboarding: {
    draft: ["active"],
    active: ["archived"],
    archived: [],
  },
};

// ─── All valid statuses per doc type ─────────────────────────────────────────

export function getValidStatuses(docType: DocType): string[] {
  const transitions = STATUS_TRANSITIONS[docType];
  return transitions ? Object.keys(transitions) : [];
}

export function getNextStatuses(docType: DocType, current: string): string[] {
  const transitions = STATUS_TRANSITIONS[docType];
  if (!transitions) return [];
  return transitions[current] ?? [];
}

// ─── Status badge colors ─────────────────────────────────────────────────────

export type StatusColor = "green" | "yellow" | "blue" | "red" | "gray" | "purple";

const STATUS_COLOR_MAP: Record<string, StatusColor> = {
  // Active / positive
  accepted: "green",
  approved: "green",
  active: "green",
  published: "green",
  stable: "green",
  delivered: "green",
  completed: "green",
  resolved: "green",
  done: "green",

  // Draft / pending
  draft: "yellow",
  proposed: "yellow",
  review: "yellow",
  discussion: "yellow",
  identified: "yellow",
  planned: "yellow",
  "needs-update": "yellow",

  // In progress
  "in-progress": "blue",
  beta: "blue",
  alpha: "blue",
  experimental: "blue",

  // Negative / terminal
  rejected: "red",
  deprecated: "red",
  cancelled: "red",
  withdrawn: "red",
  "accepted-risk": "red",

  // Neutral / archived
  archived: "gray",
  superseded: "gray",
  "escalated-to-prd": "purple",
};

export function getStatusColor(status: string): StatusColor {
  return STATUS_COLOR_MAP[status] ?? "gray";
}

export const STATUS_COLOR_CLASSES: Record<StatusColor, string> = {
  green: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  yellow: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  gray: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
  purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
};
